import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Calendar, PenLine } from "lucide-react";

export default function BlogList() {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    api.get("/blog/posts")
      .then(({ data }) => setPosts(data.results))
      .catch((e) => { toast.error(extractError(e)); setPosts([]); });
  }, []);

  return (
    <div className="min-h-screen bg-stone2-50">
      <SEO
        title="Blog · AutismBabysitters"
        description="Stories, guides, and resources for families navigating specialized care for children on the autism spectrum."
        canonical="https://autismbabysitters.com/blog"
      />
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-16">
        <span className="section-eyebrow">The blog</span>
        <h1 className="mt-2 text-4xl sm:text-5xl font-heading font-bold text-sage-900">
          Stories & guides for families on the spectrum.
        </h1>
        <p className="mt-4 text-stone2-600 max-w-2xl leading-relaxed">
          Practical resources, family stories, and hard-won insights from parents, sitters, and behavioral specialists.
        </p>

        <div className="mt-12 grid sm:grid-cols-2 gap-6" data-testid="blog-list">
          {posts === null && (
            <div className="col-span-full p-10 text-center text-stone2-500">Loading…</div>
          )}
          {posts && posts.length === 0 && (
            <div className="col-span-full paper-card p-10 text-center">
              <PenLine className="w-10 h-10 mx-auto text-stone2-300" />
              <p className="mt-3 text-stone2-600 font-heading font-semibold text-lg">No posts yet — check back soon.</p>
            </div>
          )}
          {posts && posts.map((p) => (
            <Link key={p.id}
              to={`/blog/${p.slug}`}
              data-testid={`blog-card-${p.slug}`}
              className="paper-card overflow-hidden flex flex-col hover:-translate-y-1 transition-all duration-200">
              {p.cover_image_url && (
                <img src={p.cover_image_url} alt={p.title}
                  className="w-full h-48 object-cover" loading="lazy" />
              )}
              <div className="p-5 flex-1 flex flex-col">
                <div className="text-xs text-stone2-500 flex items-center gap-2">
                  <Calendar className="w-3.5 h-3.5" />
                  {p.published_at ? new Date(p.published_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : ""}
                  {p.author_name && <span>· by {p.author_name}</span>}
                </div>
                <h2 className="mt-2 font-heading font-bold text-sage-900 text-xl leading-snug">{p.title}</h2>
                {p.excerpt && (
                  <p className="mt-2 text-sm text-stone2-600 leading-relaxed line-clamp-3">{p.excerpt}</p>
                )}
                <div className="mt-auto pt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-sage-700 group-hover:gap-2">
                  Read more <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
}
